import { ITheme, IStyle, mergeStyleSets, getTheme, MessageBarType } from '@fluentui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { AppRoutes } from '../../../app-routes';
import { InvoiceApiClient } from '../../../../services/invoice';
import { useParams } from 'react-router-dom';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { useStandardErrorState } from '../../../../entities/view-manager/error';
import { BwjButtonType } from '../../../../framework/components/bwj-button';
import { FileCabinetGraphic } from '../../../../graphics/file-cabinet';
import { CustomerInvoiceModel, ICustomerInvoiceModel } from '../../../../entities-generated/models/project/customer-invoice-model';
import { BwjNote } from '../../../../framework/components/bwj-note';
import { ProjectStatus } from '../../../../entities-generated/common/project-status';
import { toDate } from '../../../../framework/utils/date-time';
import { formatString } from '../../../../framework/utils/string';
import { BwjFormSection } from '../../../../framework/components/bwj-layout/form-section';
import { RowCol } from '../../../../framework/components/bwj-layout/rowcol';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { CartInstallationSiteListing } from '../../../cart/components/cart-install-site-listing';
import { ReceiptSummary } from '../../components/receipt-summary';
import { forEach } from '../../../../framework/utils/collection';
import { PaymentTransactionType } from '../../../../entities-generated/common/payment-transaction-type';
import { TransactionListing } from '../../components/transaction-listing';

const getStyleClasses = (theme: ITheme) => {
  const main: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '0 2rem',
    flexWrap: 'wrap',
  };

  const formMain: IStyle = {
    width: '100%',
    boxSizing: 'border-box',

    '@media(min-width: 992px)': {
      width: 1,
      flexGrow: 1,
    },

    '@media(min-width: 1200px)': {
      flexGrow: 2,
    },

    '@media(min-width: 1500px)': {
      flexGrow: 3,
    },
  };

  const formAdditional: IStyle = {
    width: '100%',
    boxSizing: 'border-box',

    '@media(min-width: 992px)': {
      width: 1,
      flexGrow: 1,
    },
  };

  const infoContainer: IStyle = {
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    margin: 0,
    marginBottom: '1.5rem',
  };

  const endBalance: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const bottomLine: IStyle = {
    margin: 0,
    padding: 0,
    borderTop: `2px solid ${theme.palette.neutralPrimary}`,
    paddingTop: '0.25em',

    '& > div': endBalance,
    '& label': {
      fontWeight: 'bold',
    },
    '& span': {
      fontWeight: 'bold',
    },
  };

  return mergeStyleSets({
    main,
    formMain,
    formAdditional,
    infoContainer,
    bottomLine,
  });
};

export const PageMyOrder: React.FunctionComponent = () => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const { id:receiptId } = useParams<{id: string }>();
  const [downloadEnabled, setDownloadEnabled] = useState(true);
  const downloadCount = useRef(0);
  const timeoutToken = useRef(0);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const errorView = useStandardErrorState();
  const [order, setOrder] = useState<ICustomerInvoiceModel>(new CustomerInvoiceModel());
  const infoMessage = useMemo(() => {
    if(order.projectStatus === ProjectStatus.CustomerBanned
        || order.projectStatus === ProjectStatus.InstallationRejected) {
      return 'This order is canceled';
    } else if (order.projectStatus === ProjectStatus.InstallationCancelled) {
      return 'Customer canceled this order';
    } else if (order.projectStatus === ProjectStatus.InstallationCompleted) {
      const delivered = formatString('{d:d-MMMdyyyy}', { d: toDate(order.installationDate) }).formattedString;
      return `<strong>Thanks for choosing us!</strong> We delivered your order on ${delivered}`;
    } else if(order.projectStatus === ProjectStatus.InstallationInProgress) {
      return '<strong>We\'re working on it!</strong> Delivery is currently in progress';
    } else if (order.projectStatus === ProjectStatus.InstallationPurchased) {
      return '<strong>Thanks for your order!</strong> We will contact you shortly to schedule your delivery and installation';;
    } else {
      const delivery = formatString('{d:d-MMMdyyyy}', { d: toDate(order.installationScheduled) }).formattedString;
      return `<strong>We're on the way!</strong> Your order is scheduled for delivery and installation on ${delivery}`;
    }
  }, [order]);
  /** - Invoice(blue): added to balance; 
   * Refund(green): added to balance
   * Disapproved Chargeback(red): added to balance */
  const runningBalance = useMemo(() => {
    const additive = [
      PaymentTransactionType.Invoice,
      PaymentTransactionType.RefundIssuance,
      PaymentTransactionType.Chargeback,
    ];
    const bals: number[] = [];
    let bal = 0;
    forEach(order.paymentTransactions, (tx, index) => {
      if(tx.transactionType !== PaymentTransactionType.ChargebackAcknowledgement
        && tx.transactionType !== PaymentTransactionType.None
      ) {
        if(additive.indexOf(tx.transactionType) > -1) {
          bal += tx.amount;
        } else {
          bal -= tx.amount;
        }
      }
      bals[index as number] = bal;
    });

    return bals;
  }, [order]);
  const endBalance = runningBalance[runningBalance.length - 1];

  useEffect(() => {
    InvoiceApiClient.getCustomerInvoice(receiptId)
    .then(resp => {
      setOrder(resp);
      setViewState(BwjViewState.Ready);
    })
    .catch((err) => {
      const vs = err === 404 ? BwjViewState.PageNotFound : BwjViewState.Error;
      setViewState(vs);
    });
  }, [receiptId]);

  const onDownloadReceipts = () => {
    InvoiceApiClient.downloadTransactionDocument(receiptId);
    setDownloadEnabled(false);
    timeoutToken.current = window.setTimeout(() => {
      downloadCount.current++;
      downloadCount.current < 3 && setDownloadEnabled(true); // prevent excessive downloads (kinda)
    }, 30000); // prevent rapid fire downloads
  }

  return (
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Getting your order...',
      }}
      errorState={errorView}
      pageNotFoundState={{
        message: 'Sorry - We can\'t find what you\'re looking for.  If you need help, please contact us',
        buttons: [{
          buttonStyle: BwjButtonType.PrimaryStandard,
          text: 'Contact us',
          onClick: () => window.location.href = 'https://bwjstorm.com/contact',
        }],
        graphic: FileCabinetGraphic,
        graphicPortraitOriented: true,
      }}
    >
      <BwjPageHeader
        title={'My Order'}
        titleIconName={AppRoutes.myOrder.icon}
        actionButtons={[{
          id: 'btnDownload',
          name: 'Download receipts/invoices',
          iconName: 'CloudDownload',
          disabled: downloadEnabled === false,
          onClick: onDownloadReceipts,
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <div className={cs.infoContainer}>
          <BwjNote
            type={MessageBarType.info}
            textContent={infoMessage}
            renderTextAsHtml={true}
          />
        </div>
        <div className={cs.main}>
          <div className={cs.formMain}>
            <BwjFormSection
              heading={'Items Purchased'}
            >
              <RowCol bottomSpacing={Spacing.Medium}>
                {order.installLocations.map(location =>
                  <CartInstallationSiteListing
                    key={location.name}
                    site={location}
                    excludeExcluded={true}
                    suppressNotification={true}
                    allowExclusion={false}
                    onExclude={() => {}}
                    onInclude={() => {}}
                    onDoorSelected={() => {}}
                    onHandleSelected={() => {}}
                  />
                )}
              </RowCol>
            </BwjFormSection>
          </div>
          <div className={cs.formAdditional}>
            <ReceiptSummary receipt={order} />
          </div>
        </div>
        <div className={cs.main}>
          <div className={cs.formMain}>
            <BwjFormSection
              heading={'Transaction History'}
            >
              <RowCol bottomSpacing={Spacing.Medium}>
                {order.paymentTransactions.map((transaction, index) =>
                  <TransactionListing
                    key={`${transaction.date}-${transaction.amount}`}
                    transaction={transaction}
                    balance={runningBalance[index]}
                  />
                )}
              </RowCol>
              <RowCol bottomSpacing={Spacing.None}>
                <div className={cs.bottomLine}>
                  <div>
                    <label>{'Balance Due'}</label>
                    <span>{formatString('{value:c#.2-USD}', { value: endBalance / 100 }).formattedString}</span>
                  </div>
                </div>
              </RowCol>
            </BwjFormSection>
          </div>
          <div className={cs.formAdditional}></div>
        </div>
      </BwjPageContent>
    </BwjViewManager>
  );
};
